import React from "react";
import { Badge, Button, Descriptions } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import PreviewDrawer from "../../../Common/PreviewDrawer";
import DeleteButton from "../../../Common/DeleteButton";
import { UserTableRow } from "../../../../hooks/api/users";
import {
  UserStatus,
  UserPlanDisplay,
  UserRoleDisplay,
  UserStatusDisplay,
} from "../../../../enums/user";
import { UserStatusType } from "../../../../store/auth";
import { dateTimeFormat } from "../../../../contstant";
import { getUserStatusColor } from "..";

import styles from "./index.module.scss";

/**
 * What the row carries beyond what its type declares.
 *
 * The list already has a Created at column reading `createdAt`, but `User` in
 * src/store/auth does not declare it — the field arrives from the API and the
 * type has never caught up. Described here rather than added there: the shared
 * type is not a designer's to change.
 *
 * TODO(design): a developer adds `createdAt` to User and this goes. See
 * DEVELOPER_TASKS.md.
 */
type UserRow = UserTableRow & { createdAt?: string };

interface UserPreviewProps {
  /** The row being previewed, or null when the drawer is closed. */
  row: UserRow | null;
  onClose: () => void;
  onEdit: (row: UserRow) => void;
  onDelete: (row: UserRow) => void;
  /** Only offered on a user who was invited and has not answered. */
  onResend: (row: UserRow) => void;
}

/**
 * A user as the list has them, read beside the list.
 *
 * Nothing is fetched: every field here is already on the row the table drew, so
 * opening a preview is a look at what is on screen rather than a second trip
 * for the same record. The editor is where the whole user lives, and this
 * offers the way there.
 *
 * Descriptions with `custom-descriptions`, the same as every other preview in
 * the app. That class is what stops the panel shifting as rows are stepped
 * through: Descriptions renders a real table, browsers measure `table-layout:
 * auto` column widths from the content, and a longer name in the next row
 * moved the whole label column. It is fixed, and the labels have a width.
 */
export default function UserPreview({
  row,
  onClose,
  onEdit,
  onDelete,
  onResend,
}: UserPreviewProps) {
  const { t } = useTranslation();

  return (
    <PreviewDrawer
      title={t("pages.users.userPreview")}
      open={row !== null}
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          <DeleteButton onClick={() => row && onDelete(row)} />

          {/* Everything this user can be done to, in the one place the user is
              shown whole — the row's actions and this footer offer the same
              set, so neither is the place where something is hidden. */}
          {row?.status === UserStatus.PENDING ? (
            <Button
              icon={<ReloadOutlined />}
              onClick={() => row && onResend(row)}
            >
              {t("pages.users.resendInvitation")}
            </Button>
          ) : null}

          <Button type="primary" onClick={() => row && onEdit(row)}>
            {t("common.edit")}
          </Button>
        </div>
      }
    >
      {row ? (
        <Descriptions bordered column={1} className="custom-descriptions">
          <Descriptions.Item label={t("pages.users.name")}>
            {row.fullName || "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("email")}>
            {row.email || "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("pages.users.role")}>
            {UserRoleDisplay[row.role as keyof typeof UserRoleDisplay] ?? "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("pages.users.plan")}>
            {UserPlanDisplay[row.plan as keyof typeof UserPlanDisplay] ?? "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("common.table.createdAt")}>
            {row.createdAt ? dayjs(row.createdAt).format(dateTimeFormat) : "-"}
          </Descriptions.Item>
          <Descriptions.Item label={t("pages.users.status")}>
            {/* The same badge the list draws, from the same colour function, so
                the row and its preview cannot describe a user differently. */}
            <Badge
              color={getUserStatusColor(row.status as UserStatusType)}
              text={
                <span className="capitalize-first-letter">
                  {UserStatusDisplay[
                    row.status as keyof typeof UserStatusDisplay
                  ] ?? row.status}
                </span>
              }
            />
          </Descriptions.Item>
        </Descriptions>
      ) : null}
    </PreviewDrawer>
  );
}
